import { T } from "../utils/theme";
import {
  LayoutDashboard, Building2, Users, Shield, Bug,
  Key, Star, LogOut, ChevronRight, Zap,
} from "lucide-react";

const NAV_SECTIONS = [
  {
    title: "OVERVIEW",
    items: [
      { id: "dashboard", label: "Dashboard",   icon: LayoutDashboard },
    ],
  },
  {
    title: "GESTION",
    items: [
      { id: "companies",  label: "Entreprises",    icon: Building2 },
      { id: "hackers",    label: "Hackers",         icon: Users },
      { id: "programmes", label: "Programmes",      icon: Shield },
      // { id: "reports",    label: "Rapports",        icon: Bug },
    ],
  },
  {
    title: "SÉCURITÉ",
    items: [
      { id: "encryption", label: "Chiffrement E2E", icon: Key },
      { id: "ranking",    label: "Classement",      icon: Star },
    ],
  },
];

// SuperAdmin extras
const SUPERADMIN_ITEMS = [
  { id: "admin-mgmt", label: "Admins",  icon: Zap },
];

// ─── Détection robuste du rôle superadmin ────────────────────────────────────
const checkSuperAdmin = (user) => {
  if (!user) return false;
  const role = (user.role || user.roles || "").toString().toLowerCase();
  return (
    role === "superadmin"       ||
    role === "super_admin"      ||
    role === "super-admin"      ||
    (Array.isArray(user.roles) && user.roles.some(r =>
      ["superadmin", "super_admin", "super-admin"].includes(r?.toLowerCase?.())
    ))
  );
};

const Sidebar = ({ user, activePage, setPage, onLogout }) => {
  const isSuperAdmin = checkSuperAdmin(user);
  const sections = isSuperAdmin
    ? [...NAV_SECTIONS, { title: "SUPER ADMIN", items: SUPERADMIN_ITEMS }]
    : NAV_SECTIONS;

  return (
    <aside style={{
      width: 230,
      background: T.surface,
      borderRight: `1px solid ${T.border}`,
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Scanline effect */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0,
        background: `repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(79,142,247,.012) 3px, rgba(79,142,247,.012) 4px)`,
      }} />

      {/* Logo */}
      <div style={{ padding: "22px 20px 20px", borderBottom: `1px solid ${T.border}`, position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 11, flexShrink: 0,
            background: `linear-gradient(135deg, ${T.blue}, ${T.purple})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 0 20px ${T.blueGlow}`,
          }}>
            <Shield size={18} color="#fff" />
          </div>
          <div>
            <div style={{ fontFamily: T.display, fontWeight: 900, fontSize: 15, color: T.text, letterSpacing: ".02em" }}>200BOUNTY</div>
            <div style={{ fontSize: 10, color: T.blue, fontWeight: 600, letterSpacing: ".08em", marginTop: 1 }}>
              {isSuperAdmin ? "⚡ SUPER ADMIN" : "🛡 ADMIN PANEL"}
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "16px 12px", overflowY: "auto", position: "relative", zIndex: 1 }}>
        {sections.map(section => (
          <div key={section.title} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".12em", color: T.muted, padding: "0 10px 8px" }}>
              {section.title}
            </div>
            {section.items.map(item => {
              const isActive = activePage === item.id;
              return (
                <div
                  key={item.id}
                  onClick={() => setPage(item.id)}
                  className={isActive ? "" : "hov-nav"}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 9, padding: "9px 12px", borderRadius: 9, marginBottom: 2,
                    cursor: "pointer", transition: "all .15s",
                    background: isActive ? `${T.blue}18` : "transparent",
                    borderLeft: `2px solid ${isActive ? T.blue : "transparent"}`,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                    <item.icon size={15} color={isActive ? T.blue : T.muted} />
                    <span style={{
                      fontSize: 12, fontWeight: isActive ? 700 : 400,
                      color: isActive ? T.text : T.textSub,
                    }}>{item.label}</span>
                  </div>
                  {isActive && <ChevronRight size={11} color={T.blue} />}
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User + Logout */}
      <div style={{ padding: "14px 16px", borderTop: `1px solid ${T.border}`, position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{
            width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
            background: `linear-gradient(135deg, ${T.purple}, ${T.blue})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 800, color: "#fff",
            boxShadow: `0 0 12px ${T.purple}50`,
          }}>
            {(user?.nom || user?.email || "A")[0].toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {user?.nom || user?.prenom || user?.email?.split("@")[0] || "Admin"}
            </div>
            <div style={{ fontSize: 10, color: isSuperAdmin ? T.purple : T.blue, fontWeight: 600 }}>
              {isSuperAdmin ? "Super Admin" : "Administrateur"}
            </div>
          </div>
        </div>
        <button onClick={onLogout} className="btn btn-ghost" style={{ width: "100%", justifyContent: "center", fontSize: 12 }}>
          <LogOut size={13} /> Déconnexion
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;